import { FullPageLoader } from "@components/loaders/FullPageLoader.component";
import { DashboardIcon } from "@components/pipelineNodes/PipelineNodeIcon.component";
import TenantPicker from "@components/tenant/TenantPicker.component";
import { useAuth, useAuthActions } from "@frontegg/react";
import SourceORM, { Source, useSourceByType } from "@models/source";
import { WizardStep, WizardStepDivider, WizardSteps } from "@pages/PageStructure.component";
import { useCallback, useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import PliableLogo from '@assets/images/pliable/brand_mark_orange.png';
import AuthService, { useAuthState, useFullAuthState } from "@services/auth/auth.service";
import TenantService from "@services/tenant/tenant.service";
import { InlineWidget, useCalendlyEventListener } from "react-calendly";
import SuccessAlert from "@components/statusIndicators/SuccessAlert.component";
import BookOnboardingCallPage, { CalendlyEventInfo } from "@components/onboarding/OnboardingCall.component";
import BookOnboardingCallModal from "@components/onboarding/OnboardingCall.component";
import ConfigService from "@services/config/config.service";

/**
 * 
 * Test URL: '/connect/plb_shopify?hmac=0970aa0627890211e43c9ebbc3fab5bead9ec239926c1e8367c3789cbbd82327&host=YWRtaW4uc2hvcGlmeS5jb20vc3RvcmUvcWJxeXZlLW5k&session=1a01517d40807ed5ce1caa7bf143da104fa026612fc87acde251cb2a1a2bea90&shop=qbqyve-nd.myshopify.com&timestamp=1746538554'
 */
const BeginConnectorTypePage = () => {
    const { connectorType } = useParams();

    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const authActions = useAuthActions();

    const { authState, isAuthenticated, authStateUpdatedAt } = useFullAuthState();

    const upsertConnector = useCallback(async (connectorType: string, comeBackHere: boolean = true) => {
        const connectParams = JSON.parse(localStorage.getItem('connect_params') || '{}');
        const resp = await SourceORM.upsertConnectorForType(connectParams.connectorType, connectParams.searchParams);

        if (comeBackHere) {
            console.log('setting return path');
            localStorage.setItem('returnPath', `/connect/${connectParams.connectorType}`);
        }

        if (resp.authorization_url) {
            window.location.href = resp.authorization_url;
        } else {
            navigate(`/source/${resp.source.id}`);
        }
        
    }, []);

    const [existingSource, setExistingSource] = useState<Source|undefined>(undefined);
    const loadExistingSource = useCallback(async () => {
        if (!isAuthenticated) {
            return;
        }
        const source = await SourceORM.getSourceForType(connectorType as string);
        setExistingSource(source ? source : undefined);
    }, [connectorType, authState, isAuthenticated])

    useEffect(() => {
        localStorage.setItem('connect_params', JSON.stringify({
            connectorType,
            searchParams: Object.fromEntries(searchParams.entries()),
        }))
    }, [connectorType, searchParams]);


    

    // Todo: load the connector and if it exists, just run the upsert connector and no-click authentication flow.
    // If it doesn't exist, then consider them "new" and walk them through the onboarding

    const tenantName = AuthService.getInstance().getTenantName();

    const location = useLocation();
    const thisURI = location.pathname + location.search;

    const doLogout = useCallback(() => {
        localStorage.setItem('returnPath', thisURI);
        navigate(`/do/logout`);
    }, [thisURI]);

    const doLogin = useCallback(() => {
        localStorage.setItem('returnPath', thisURI);
        navigate('/login');
    }, [thisURI]);

    const doSignup = useCallback(() => {
        localStorage.setItem('returnPath', thisURI);
        navigate('/account/sign-up');
    }, [thisURI]);

    // useEffect(() => {
    //     if (connectorType) {
    //         upsertConnector(connectorType);
    //     }
    // }, [searchParams, connectorType]);

    const [showBookCall, setShowBookCall] = useState(false);

    const [bookedCallLink, setBookedCallLink] = useState('');
    const checkIfBookedCall = useCallback(async () => {
        if (!isAuthenticated) {
            setBookedCallLink('');
            return;
        }

        // Reload the config to check if we have the event link
        await ConfigService.getInstance().loadConfig();
        setBookedCallLink(ConfigService.getInstance().config?.onboarding_info?.onboardingEventLink || '');
    }, []);

    const closeBookingModal = useCallback(() => {
        setShowBookCall(false);
        checkIfBookedCall();
    }, [checkIfBookedCall]);

    useEffect(() => {
        loadExistingSource();
        checkIfBookedCall();
    }, []);

    return <div className="d-flex">
        <BookOnboardingCallModal show={showBookCall} onHide={closeBookingModal}/>
        <div style={{width: '50%', height: '100vh'}} className=" border-right bg-white">
            <div className="p-5">
                <div className="d-flex center-vertically mb-3">
                    <img src={PliableLogo} className="me-3" style={{width: '60px'}}/>
                    <div className="flex-1">
                        <h1 className="mb-0" style={{fontSize: '3rem'}}>Welcome to Pliable.</h1>
                        <div style={{fontSize: '1.5rem'}}>Let's get you some dashboards!</div>
                    </div>
                </div>
                {connectorType == 'plb_shopify' && <>
                    <div className="list-group">
                        <div className="list-group-item p-3">
                            <div className="d-flex center-vertically">
                                <div className="me-2">
                                    <DashboardIcon
                                        icon="mdi mdi-check-bold"
                                        bgColor="success"
                                    />
                                </div>
                                <div className="flex-1">
                                    <h2 className="mb-0">1. Install Shopify App.</h2>
                                    <div>Success! You've installed the app in your Shopify store.</div>
                                </div>
                                
                            </div>
                        </div>
                        <div className="list-group-item p-3">
                            {isAuthenticated && <div className="d-flex">
                                <div className="me-2">
                                    <DashboardIcon
                                        icon="mdi mdi-check-bold"
                                        bgColor="success"
                                    />
                                </div>
                                <div className="flex-1">
                                    <h2 className="mb-0">2. Configure your Pliable account.</h2>
                                    <p>You are logged in as <strong>{authState?.email}</strong> in the <strong>{tenantName}</strong> account.</p>
                                    <button className="btn btn-outline-primary me-1" onClick={doLogout}>
                                        <i className="mdi mdi-logout"></i> Logout
                                    </button>
                                    {/* <a role="button" className="">
                                        <i className="mdi mdi-swap-horizontal"></i> Install Shopify in a different account
                                    </a> */}
                                </div>
                            </div>}
                            {!isAuthenticated && <div className="d-flex">
                                <div className="me-2">
                                    <DashboardIcon
                                        icon="mdi mdi-cog"
                                        bgColor="secondary"
                                    />
                                </div>
                                <div className="flex-1">
                                    <h2 className="">2. Configure your Pliable account.</h2>
                                    <div>
                                        <div className="mb-2">
                                        If you already have a Pliable account, please log in to that account to continue. If not, just click on "Create Account".
                                        </div>
                                        <div>
                                            
                                            <button className="btn btn-primary me-2" onClick={doSignup}>Create Account</button>
                                            <button className="btn btn-light" onClick={doLogin}>I have an account.</button>
                                        </div>
                                    </div>
                                </div>
                                
                            </div>}
                        </div>
                        <div className="list-group-item p-3">
                            <div className="d-flex center-vertically">
                                <div className="me-2">
                                    {existingSource && <DashboardIcon
                                        icon="mdi mdi-check-bold"
                                        bgColor="success"
                                    />}
                                    {!existingSource && <DashboardIcon
                                        icon="mdi mdi-connection"
                                        bgColor="secondary"
                                    />}
                                    
                                </div>
                                <div className="flex-1">
                                    {isAuthenticated && <div>
                                        <h2 className="mb-0">3. Connect Shopify to Pliable</h2>
                                        {!existingSource && <>
                                                <div>Now that you've installed the app and created your Pliable account, click the button below to finalize the connection between Shopify and Pliable.</div>
                                            <button disabled={!isAuthenticated}

                                                className="btn btn-pliable" onClick={() => upsertConnector(connectorType)}
                                            >
                                                <i className="mdi mdi-connection"></i> Connect Shopify
                                            </button>
                                        </>}
                                        {existingSource && <>
                                            <div><strong>Nice!</strong> Everything is connected.</div>
                                        </>}
                                       
                                    </div>}
                                    {!isAuthenticated && <div>
                                            <h2 className="mb-0 text-muted">3. Connect Shopify to Pliable</h2>
                                    </div>}
                                    
                                   
                                </div>
                                
                            </div>
                        </div>
                        <div className="list-group-item p-3">
                            <div className="d-flex">
                                <div className="me-2">
                                    {bookedCallLink && <DashboardIcon
                                        icon="mdi mdi-check-bold"
                                        bgColor="success"
                                    />}
                                    {!bookedCallLink && <DashboardIcon
                                        icon="mdi mdi-phone"
                                        bgColor="secondary"
                                    />}
                                    
                                </div>
                                <div className="flex-1">
                                    {existingSource && <div>
                                        <h2 className="mb-0">4. Book a call with a product expert</h2>
                                        {!bookedCallLink && <>
                                                <div>Now just book a call with a product expert to get your dashboards up and running and get a tour of Pliable.</div>
                                            <button

                                                className="btn btn-pliable" onClick={() => setShowBookCall(true)}
                                            >
                                                <i className="mdi mdi-phone"></i> Book a call
                                            </button>
                                        </>}
                                        {bookedCallLink && <>
                                            <div><strong>Nice!</strong> Your call is booked.</div>
                                            <CalendlyEventInfo eventUuid={bookedCallLink}/>
                                        </>}
                                       
                                    </div>}
                                    {!existingSource && <div>
                                            <h2 className="mb-0 text-muted">4. Book a call with a product expert</h2>
                                    </div>}
                                    
                                   
                                </div>
                                
                            </div>
                        </div>
                        
                    </div>
                </>}
                
                

            </div>
        </div>
        <div style={{width: '50%'}} className="col-6 bg-navy text-white">
            <div className="p-3">
                <div className="d-flex justify-content-end align-items-center">
                    <button className="btn btn-pliable me-2">
                        <i className="mdi mdi-chat"></i> Get Help
                    </button>
                    {isAuthenticated && <>
                        <TenantPicker/>
                        <Link to="/logout" className="btn btn-link text-white">Logout</Link>
                    </>}
                    
                </div>
            </div>
            <div className="p-5">
                
            </div>
            {/* <div className="p-5">
                What goes here?
            </div> */}
        </div>
    </div>
    return <FullPageLoader />
}

export default BeginConnectorTypePage;